import {getAttributeOrThrow, getViewport, qsaRequired, qsRequired} from "@/scripts/core/global";
import {UcoastEl} from '@/scripts/core/UcoastEl'

type TabItem = {
    navItem: HTMLElement
    contentItem: HTMLElement
    mobileNavItem: HTMLElement
}

export class ProductTabs extends UcoastEl {
    static htmlSelector = 'product-tabs'
    // add typescript types for this.navItems etc
    navItems: NodeListOf<HTMLElement>
    navContainer: HTMLElement
    mobileNavItems: NodeListOf<HTMLElement>
    contentItems: NodeListOf<HTMLElement>
    contentContainer: HTMLElement
    activeBg: HTMLElement

    constructor() {
        super();
        this.navItems = qsaRequired('[data-tab-nav]', this)
        this.navContainer = qsRequired('[data-tab-nav-container]', this)
        this.mobileNavItems = qsaRequired('[data-tab-mobile-nav]', this)
        this.contentItems = qsaRequired('[data-tab-content]', this)
        this.contentContainer = qsRequired('[data-tabs-container]', this)
        this.activeBg = qsRequired('[data-tab-active-bg]', this)

        if (!this.navItems || !this.contentItems) return;

        this.initItems();
        const resizeObserver = new ResizeObserver((_: ResizeObserverEntry[]) => this.initItems());
        resizeObserver.observe(this.contentContainer);

        for (let i = 0; i < this.navItems.length; i++) {
            this.navItems[i].addEventListener('click', this.onButtonClick.bind(this));
            this.mobileNavItems[i].addEventListener('click', this.onButtonClick.bind(this));
        }
    }

    initItems() {
        this.classList.add('loaded');
        this.setHeight();
    }

    setHeight() {
        const {contentItem, navItem} = this.getActiveItem();
        if (getViewport() === 'm-up') {
            this.classList.add('loaded');
            this.contentContainer.style.height = `${contentItem.offsetHeight}px`;

            const leftDistance = navItem.getBoundingClientRect().left - this.navContainer.getBoundingClientRect().left;
            this.activeBg.style.width = `${navItem.offsetWidth}px`;
            this.activeBg.style.transform = `translateX(${leftDistance}px)`;
            contentItem.removeAttribute('style');
        } else {
            this.contentContainer.style.height = 'unset';
            contentItem.style.maxHeight = `${contentItem.scrollHeight}px`;
        }
    }

    getSelectedItem(i:number): TabItem {
        if (!this.navItems[i] || !this.contentItems[i] || !this.mobileNavItems[i]) throw new Error('Tab item not found');
        return {
            navItem: this.navItems[i],
            contentItem: this.contentItems[i],
            mobileNavItem: this.mobileNavItems[i],
        };
    }

    getActiveItem() {
        let activeItem = this.getSelectedItem(0);
        this.navItems.forEach((el: HTMLElement, i: number) => {
            if (el.getAttribute('aria-current') !== 'true') return;
            activeItem = this.getSelectedItem(i);
        });
        return activeItem;
    }

    setActive(item: TabItem) {
        item.navItem.setAttribute('aria-current', 'true');
        item.contentItem.setAttribute('aria-expanded', 'true');
        item.mobileNavItem.setAttribute('aria-current', 'true');
    }

    setHidden(item: TabItem) {
        item.navItem.removeAttribute('aria-current');
        item.contentItem.removeAttribute('aria-expanded');
        item.contentItem.removeAttribute('style');
        item.mobileNavItem.removeAttribute('aria-current');
    }

    onButtonClick(event: MouseEvent | TouchEvent) {
        event.preventDefault();
        if (!event.currentTarget || !(event.currentTarget instanceof HTMLElement)) return;
        const id = getAttributeOrThrow( 'data-tab', event.currentTarget)

        this.navItems.forEach((el: HTMLElement, i: number) => {
            const item = this.getSelectedItem(i);
            if (el.getAttribute('data-tab') !== id) {
                this.setHidden(item);
                return;
            }

            if (el.getAttribute('aria-current') === 'true' && getViewport() === 'm-down') {
                this.setHidden(item);
                return;
            }

            this.setActive(item);
        });

        this.setHeight();
    }
}
